import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export interface DueDateGradeSyncModalProps {
  /** Course is on the native Canvas API grade-push path. */
  gradePushReady: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const tPrefix = "modals.dueDateGradeSync";

/**
 * Shown after a due-date change on a campaign whose grades already went to the
 * LMS (FL-187).
 *
 * The two grade-push paths need different advice. On the native Canvas API path
 * we post the real submission time, so Canvas recomputes late status from the
 * new due date on its own and a re-sync is merely optional. On the LTI AGS path
 * the LMS recorded the *push* time as the submission time, so re-syncing stamps
 * submissions with today's date — worth warning about until FL-322 lands.
 */
export const DueDateGradeSyncModal: React.FC<DueDateGradeSyncModalProps> = ({
  gradePushReady,
  onCancel,
  onConfirm,
}) => {
  const [t] = useTranslation();

  const body = gradePushReady
    ? t(`${tPrefix}.bodyNative`)
    : t(`${tPrefix}.bodyLti`);
  const confirmLabel = gradePushReady
    ? t(`${tPrefix}.confirm`)
    : t(`${tPrefix}.confirmLti`);
  const dismissLabel = gradePushReady
    ? t(`${tPrefix}.dismiss`)
    : t(`${tPrefix}.dismissLti`);

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      aria-labelledby="due-date-grade-sync-title"
    >
      <DialogTitle id="due-date-grade-sync-title">
        {t(`${tPrefix}.title`)}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{body}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          color="light"
          // On the LTI path the safe choice is to do nothing, so it takes focus
          autoFocus={!gradePushReady}
        >
          {dismissLabel}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          autoFocus={gradePushReady}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
